// The cabinet guide: which sections exist, what the nav tour walks through,
// how a URL maps to a section, and the step-by-step walk over each page's
// own controls. Titles reuse the accountMenu.* nav labels so the tour always
// matches the sidebar; bodies live under accountTour.*.

export type PageSection = {
    key: string;
    titleKey: string;
    bodyKey: string;
};

// URL segment under /:lang/account → section. "" is the cabinet home.
export const PAGE_SECTIONS: Record<string, PageSection> = {
    "": { key: "home", titleKey: "accountMenu.home", bodyKey: "accountTour.page.home" },
    consultations: { key: "consultations", titleKey: "accountMenu.consultations", bodyKey: "accountTour.page.consultations" },
    "consultations-email": { key: "consultations-email", titleKey: "accountMenu.profile", bodyKey: "accountTour.page.consultationsEmail" },
    diagnose: { key: "diagnose", titleKey: "accountMenu.diagnose", bodyKey: "accountTour.page.diagnose" },
    diet: { key: "diet", titleKey: "accountMenu.diet", bodyKey: "accountTour.page.diet" },
    trees: { key: "trees", titleKey: "accountMenu.trees", bodyKey: "accountTour.page.trees" },
    area_of_life: { key: "area_of_life", titleKey: "accountMenu.area_of_life", bodyKey: "accountTour.page.areaOfLife" },
    questions: { key: "questions", titleKey: "accountMenu.questions", bodyKey: "accountTour.page.questions" },
    lectures: { key: "lectures", titleKey: "accountMenu.lectures", bodyKey: "accountTour.page.lectures" },
    workouts: { key: "workouts", titleKey: "accountMenu.workouts", bodyKey: "accountTour.page.workouts" },
    analytics: { key: "analytics", titleKey: "accountMenu.analytics", bodyKey: "accountTour.page.analytics" },
    "good-deeds": { key: "good-deeds", titleKey: "accountMenu.good_deeds", bodyKey: "accountTour.page.goodDeeds" },
    community: { key: "community", titleKey: "accountMenu.community", bodyKey: "accountTour.page.community" },
    reviews: { key: "reviews", titleKey: "accountMenu.reviews", bodyKey: "accountTour.page.reviews" },
    // "activities" is gone: /account/activities now redirects to /account/community,
    // so the tour never lands there.
    affiliate: { key: "affiliate", titleKey: "ogt.navbar.affiliate_program", bodyKey: "accountTour.page.affiliate" },
    "my-courses": { key: "my-courses", titleKey: "accountMenu.my_courses", bodyKey: "accountTour.page.myCourses" },
    "my-courses-new": { key: "my-courses", titleKey: "accountMenu.my_courses", bodyKey: "accountTour.page.myCourses" },
    notifications: { key: "notifications", titleKey: "accountMenu.notifications", bodyKey: "accountTour.page.notifications" },
    profile: { key: "profile", titleKey: "accountMenu.profile", bodyKey: "accountTour.page.profile" },
    settings: { key: "settings", titleKey: "accountMenu.settings", bodyKey: "accountTour.page.settings" },
};

export type NavStep = {
    id: string;
    // data-tour attribute value in the NavBar; null = centered card.
    anchor: string | null;
    titleKey: string;
    bodyKey: string;
};

// The first-visit walk over the sidebar, in visual order.
export const NAV_STEPS: NavStep[] = [
    { id: "intro", anchor: null, titleKey: "accountTour.intro.title", bodyKey: "accountTour.intro.body" },
    { id: "home", anchor: "home", titleKey: "accountMenu.home", bodyKey: "accountTour.nav.home" },
    { id: "consultations", anchor: "group-consultations", titleKey: "accountMenu.consultations", bodyKey: "accountTour.nav.consultations" },
    { id: "questions", anchor: "group-questions", titleKey: "accountMenu.questions", bodyKey: "accountTour.nav.questions" },
    { id: "workouts", anchor: "group-workouts", titleKey: "accountMenu.workouts", bodyKey: "accountTour.nav.workouts" },
    { id: "community", anchor: "group-community-group", titleKey: "accountMenu.community", bodyKey: "accountTour.nav.community" },
    { id: "courses", anchor: "group-courses", titleKey: "accountMenu.my_courses", bodyKey: "accountTour.nav.courses" },
    { id: "other", anchor: "group-other", titleKey: "accountMenu.other", bodyKey: "accountTour.nav.other" },
    { id: "done", anchor: null, titleKey: "accountTour.done.title", bodyKey: "accountTour.done.body" },
];

export type PageStep = {
    id: string;
    // data-tour attribute value on the page; null = centered card.
    // Steps whose element never appears (empty state, not loaded, other
    // viewport) are skipped by the tour automatically.
    anchor: string | null;
    titleKey: string;
    bodyKey: string;
};

const step = (section: string, id: string, anchor: string): PageStep => ({
    id,
    anchor,
    titleKey: `accountTour.step.${section}.${id}.t`,
    bodyKey: `accountTour.step.${section}.${id}.b`,
});

// The element-by-element walk of each page, in visual/usage order.
// (The overview card from PAGE_SECTIONS is always prepended as step 1.)
const PAGE_ELEMENT_STEPS: Record<string, PageStep[]> = {
    home: [
        step("home", "courses", "home-courses"),
        step("home", "empty", "home-empty"),
    ],
    consultations: [
        step("consultations", "card", "consultations-status-card"),
        step("consultations", "progress", "consultations-progress"),
        step("consultations", "actions", "consultations-actions"),
        step("consultations", "new", "consultations-new"),
        step("consultations", "email", "consultations-email"),
        step("consultations", "notify", "consultations-notify"),
    ],
    "consultations-email": [
        step("consultations-email", "current", "consult-email-current"),
        step("consultations-email", "change", "consult-email-change"),
    ],
    diagnose: [
        step("diagnose", "intro", "diagnose-intro"),
        step("diagnose", "chart", "diagnose-chart"),
        step("diagnose", "legend", "diagnose-legend"),
        step("diagnose", "review", "reviews-banner"),
    ],
    diet: [
        step("diet", "search", "diet-search"),
        step("diet", "filters", "diet-filters"),
        step("diet", "products", "diet-products"),
        step("diet", "instruction", "diet-instruction"),
        step("diet", "review", "reviews-banner"),
    ],
    trees: [
        step("trees", "intro", "trees-intro"),
        step("trees", "grid", "trees-grid"),
    ],
    area_of_life: [
        step("area_of_life", "legend", "area-legend"),
        step("area_of_life", "ranges", "area-ranges"),
        step("area_of_life", "info", "area-info"),
    ],
    questions: [
        step("questions", "input", "questions-input"),
        step("questions", "player", "questions-player"),
        step("questions", "history", "questions-history"),
        step("questions", "tokens", "questions-tokens"),
        step("questions", "share", "questions-share"),
    ],
    lectures: [
        step("lectures", "tabs", "lectures-tabs"),
        step("lectures", "search", "lectures-search"),
    ],
    // Progress and Points moved to /account/analytics, so the workouts page
    // is the calendar alone and has no tab row any more: the walk points at
    // the month grid itself.
    workouts: [
        step("workouts", "calendar", "calendar-month"),
        // The Table view is still hidden on WorkoutsPage — restore this step
        // (and a tab row to anchor it) together with it.
        // step("workouts", "table", "workouts-tab-table"),
    ],
    analytics: [
        step("analytics", "tabs", "analytics-tabs"),
        step("analytics", "analytics", "analytics-tab-analytics"),
        step("analytics", "points", "analytics-tab-points"),
    ],
    "good-deeds": [
        step("good-deeds", "date", "good-deeds-date"),
        step("good-deeds", "categories", "good-deeds-categories"),
        step("good-deeds", "actions", "good-deeds-actions"),
        step("good-deeds", "progress", "good-deeds-progress"),
    ],
    community: [
        step("community", "search", "community-search"),
        step("community", "nav", "community-nav"),
        step("community", "groups", "community-groups"),
        step("community", "identity", "community-identity"),
    ],
    reviews: [
        step("reviews", "list", "reviews-list"),
        step("reviews", "empty", "reviews-empty"),
    ],
    affiliate: [
        step("affiliate", "handle", "affiliate-handle"),
        step("affiliate", "balance", "affiliate-balance"),
        step("affiliate", "course", "affiliate-course"),
        step("affiliate", "ledger", "affiliate-ledger"),
    ],
    "my-courses": [
        step("my-courses", "card", "my-courses-card"),
    ],
    notifications: [
        step("notifications", "list", "notifications-list"),
        step("notifications", "empty", "notifications-empty"),
        step("notifications", "pagination", "notifications-pagination"),
    ],
    profile: [
        step("profile", "data", "profile-data"),
    ],
    // Резервный код для часов — в «Настройках › Часы» (владелец, 03.10.2026), над уведомлениями.
    settings: [
        step("settings", "watchCode", "settings-watch-code"),
        step("settings", "push", "settings-push"),
        step("settings", "categories", "settings-categories"),
    ],
};

const SECTION_BY_KEY: Record<string, PageSection> = Object.values(
    PAGE_SECTIONS,
).reduce(
    (acc, section) => {
        acc[section.key] = section;
        return acc;
    },
    {} as Record<string, PageSection>,
);

/** The full walk for a page: overview card first, then its elements. */
export function pageStepsFor(sectionKey: string): PageStep[] {
    const section = SECTION_BY_KEY[sectionKey];
    if (!section) return [];
    return [
        {
            id: "overview",
            anchor: null,
            titleKey: section.titleKey,
            bodyKey: section.bodyKey,
        },
        ...(PAGE_ELEMENT_STEPS[sectionKey] || []),
    ];
}

/** Section for a /:lang/account/... pathname, or null when not recognised. */
export function sectionFromPathname(pathname: string): PageSection | null {
    const segments = pathname.replace(/\/+$/, "").split("/").filter(Boolean);
    const accountIdx = segments.indexOf("account");
    if (accountIdx === -1) return null;
    const segment = segments[accountIdx + 1] || "";
    return PAGE_SECTIONS[segment] || null;
}

// A page that covers the cabinet chrome (and with it the floating "?" button)
// asks for its walk through this event — e.g. the phone app of
// /account/analytics has its own "?" in its top bar.
export const START_PAGE_TOUR_EVENT = "account-tour-start-page";
export const startPageTour = () =>
    window.dispatchEvent(new Event(START_PAGE_TOUR_EVENT));

/**
 * When /account/analytics is the full-screen phone app (AnalyticsApp) instead
 * of the page with tabs: a narrow window, or a phone turned sideways (a short
 * touch-only screen — 844×390 is wider than 768 but still the same phone,
 * and turning it must not swap the app for the site page; `hover: none`
 * keeps touch laptops with a short window on the page). One query for the
 * page and for the tour below.
 */
export const ANALYTICS_APP_QUERY = "(max-width: 768px), (max-height: 500px) and (pointer: coarse) and (hover: none)";

export const isAnalyticsAppViewport = (): boolean =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(ANALYTICS_APP_QUERY).matches;

/**
 * Where the sidebar walk must not run: on a phone /account/analytics is a
 * full-screen app laid over the cabinet header (AnalyticsApp), so the
 * burger drawer the walk opens stays hidden under it and its spotlights
 * would outline the app instead of menu items. The walk waits for the next
 * cabinet page instead.
 */
export const navTourHiddenOn = (sectionKey: string | undefined, appViewport: boolean) =>
    sectionKey === "analytics" && appViewport;

const NAV_SEEN_KEY = "account_tour_nav_seen";

export const isNavTourSeen = () =>
    localStorage.getItem(NAV_SEEN_KEY) === "1";
export const markNavTourSeen = () =>
    localStorage.setItem(NAV_SEEN_KEY, "1");

// v2: bumped suffix so the richer per-element walks re-offer themselves
// once to users who had already seen the old single-card explainers.
export const isPageSeen = (key: string) =>
    localStorage.getItem(`account_tour_page2_${key}`) === "1";
export const markPageSeen = (key: string) =>
    localStorage.setItem(`account_tour_page2_${key}`, "1");
